import React, { useEffect, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiLock, FiShield, FiSearch, FiDownload } from 'react-icons/fi';
import Layout from '../components/Layout';
import API from '../api/axios';
import { toast } from 'react-toastify';
import './Projects.css';
import './Roles.css';

const ACTIONS = ['view', 'create', 'edit', 'delete'];

const ACTION_COLORS = {
  view:   { bg: '#eff6ff', color: '#3b82f6' },
  create: { bg: '#f0fdf4', color: '#10b981' },
  edit:   { bg: '#fffbeb', color: '#f59e0b' },
  delete: { bg: '#fef2f2', color: '#ef4444' },
};

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', modulePermissions: [], requireClockInPhoto: false, staffType: 'office' });
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState(null);

  const fetchAll = () => {
    API.get('/roles').then(r => setRoles(r.data)).catch(() => {});
    API.get('/modules').then(r => setModules(r.data)).catch(() => {});
  };

  useEffect(() => { fetchAll(); }, []);

  const openModal = (role = null) => {
    setEditing(role);
    const perms = modules.map(mod => {
      const existing = role?.modulePermissions?.find(mp => mp.module?._id === mod._id || mp.module === mod._id);
      return {
        module: mod._id,
        moduleName: mod.name,
        permissions: existing?.permissions || { view: false, create: false, edit: false, delete: false },
      };
    });
    setForm({ name: role?.name || '', modulePermissions: perms, requireClockInPhoto: role?.requireClockInPhoto || false, staffType: role?.staffType || 'office' });
    setShowModal(true);
  };

  const togglePerm = (modId, action) => {
    setForm(prev => ({
      ...prev,
      modulePermissions: prev.modulePermissions.map(mp =>
        mp.module === modId
          ? { ...mp, permissions: { ...mp.permissions, [action]: !mp.permissions[action] } }
          : mp
      ),
    }));
  };

  const toggleAll = (action) => {
    const allOn = form.modulePermissions.every(mp => mp.permissions[action]);
    setForm(prev => ({
      ...prev,
      modulePermissions: prev.modulePermissions.map(mp => ({
        ...mp, permissions: { ...mp.permissions, [action]: !allOn },
      })),
    }));
  };

  const toggleRow = (modId) => {
    setForm(prev => {
      const row = prev.modulePermissions.find(mp => mp.module === modId);
      const allOn = ACTIONS.every(a => row.permissions[a]);
      return {
        ...prev,
        modulePermissions: prev.modulePermissions.map(mp =>
          mp.module === modId
            ? { ...mp, permissions: ACTIONS.reduce((acc, a) => ({ ...acc, [a]: !allOn }), {}) }
            : mp
        ),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        requireClockInPhoto: form.requireClockInPhoto,
        staffType: form.staffType,
        modulePermissions: form.modulePermissions.map(mp => ({
          module: mp.module,
          permissions: mp.permissions,
        })),
      };
      if (editing) {
        await API.put(`/roles/${editing._id}`, payload);
        toast.success('Role updated');
      } else {
        await API.post('/roles', payload);
        toast.success('Role created');
      }
      setShowModal(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving role');
    }
  };

  const handleDelete = (role) => setConfirm({ role });
  const doDelete = async () => {
    const { role } = confirm; setConfirm(null);
    try { await API.delete(`/roles/${role._id}`); toast.success('Role deleted'); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.message || 'Error deleting role'); }
  };

  const exportPDF = () => {
    const logoUrl = `${window.location.origin}/cmpy_logo.png`;
    const rows = filtered.map((role, i) => {
      const modCount = role.modulePermissions?.filter(mp => Object.values(mp.permissions || {}).some(Boolean)).length || 0;
      return `<tr><td>${i + 1}</td><td>${role.name}</td><td>${role.isAdmin ? 'Full Access' : 'Custom'}</td><td>${role.isSystem ? 'Yes' : 'No'}</td><td>${modCount} modules</td></tr>`;
    }).join('');
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>Roles List</title><style>
      body{font-family:Arial,sans-serif;padding:24px;font-size:12px}
      .header{display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid #2563eb;padding-bottom:12px;margin-bottom:16px}
      .header img{height:44px;object-fit:contain}
      table{width:100%;border-collapse:collapse}
      th{background:#2563eb;color:#fff;padding:7px 8px;text-align:left;font-size:10px}
      td{padding:5px 8px;border-bottom:1px solid #f1f5f9;font-size:11px}
      @media print{button{display:none}}
    </style></head><body>
      <div class="header">
        <div style="display:flex;align-items:center;gap:12px">
          <img src="${logoUrl}" alt="" onerror="this.style.display='none'" />
          <div><div style="font-size:17px;font-weight:700">Interior Design Co.</div><div style="font-size:11px;color:#64748b">Roles & Permissions</div></div>
        </div>
      </div>
      <table><thead><tr><th>#</th><th>Role Name</th><th>Access Type</th><th>System Role</th><th>Permissions</th></tr></thead>
      <tbody>${rows}</tbody></table>
      <script>window.onload=()=>{window.print();}${'<'}/script>
    </body></html>`);
    win.document.close();
  };

  const filtered = roles.filter(r => r.name?.toLowerCase().includes(search.toLowerCase()));
  const customRoles = roles.filter(r => !r.isSystem).length;

  return (
    <Layout>
      <div className="page">
        <div className="page-actions">
          <div style={{ display: 'flex', gap: '.4rem' }}>
            <button className="btn-outline-sm" onClick={exportPDF}><FiDownload /> Export</button>
            <button className="btn-primary-sm" onClick={() => openModal()}><FiPlus /> New Role</button>
          </div>
        </div>

        {/* Stats */}
        <div className="role-stats">
          <div className="role-stat-card">
            <div className="role-stat-icon" style={{ background: '#eff6ff', color: '#3b82f6' }}><FiShield /></div>
            <div>
              <p className="user-stat-num">{roles.length}</p>
              <p className="user-stat-label">Total Roles</p>
            </div>
          </div>
          <div className="role-stat-card">
            <div className="role-stat-icon" style={{ background: '#fef3c7', color: '#f59e0b' }}><FiLock /></div>
            <div>
              <p className="user-stat-num">{roles.filter(r => r.isAdmin).length}</p>
              <p className="user-stat-label">Admin Roles</p>
            </div>
          </div>
          <div className="role-stat-card">
            <div className="role-stat-icon" style={{ background: '#f0fdf4', color: '#10b981' }}><FiShield /></div>
            <div>
              <p className="user-stat-num">{roles.filter(r => r.isSystem).length}</p>
              <p className="user-stat-label">System Roles</p>
            </div>
          </div>
          <div className="role-stat-card">
            <div className="role-stat-icon" style={{ background: '#f5f3ff', color: '#8b5cf6' }}><FiShield /></div>
            <div>
              <p className="user-stat-num">{customRoles}</p>
              <p className="user-stat-label">Custom Roles</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="roles-toolbar">
          <div className="search-box">
            <FiSearch />
            <input placeholder="Search roles..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <span className="result-count">{filtered.length} role{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Roles grid */}
        <div className="roles-grid">
          {filtered.map(role => (
            <div key={role._id} className={`role-card ${role.isAdmin ? 'role-card-admin' : ''}`}>
              <div className="role-card-header">
                <div className="role-title">
                  <div className="role-icon" style={role.isAdmin
                    ? { background: '#eff6ff', color: '#3b82f6' }
                    : { background: '#f5f3ff', color: '#8b5cf6' }}>
                    <FiShield />
                  </div>
                  <div>
                    <h3>{role.name}</h3>
                    <div className="role-badges">
                      {role.isAdmin && <span className="admin-badge">Full Access</span>}
                      {role.isSystem && <span className="system-badge">System</span>}
                      {!role.isSystem && <span className="custom-badge">Custom</span>}
                      <span className="custom-badge">{role.staffType === 'site' ? 'Site Staff' : 'Office Staff'}</span>
                    </div>
                  </div>
                </div>
                <div className="card-actions">
                  {!role.isAdmin && (
                    <button className="action-btn edit" onClick={() => openModal(role)}><FiEdit2 /></button>
                  )}
                  {!role.isSystem && (
                    <button className="action-btn delete" onClick={() => handleDelete(role)}><FiTrash2 /></button>
                  )}
                </div>
              </div>

              {role.isAdmin ? (
                <div className="admin-access-banner">
                  <FiLock />
                  <span>Full access to all modules — permissions not editable</span>
                </div>
              ) : (
                <div className="role-modules">
                  {role.modulePermissions?.filter(mp => Object.values(mp.permissions).some(Boolean)).length > 0 ? (
                    <>
                      <p className="modules-count">
                        {role.modulePermissions.filter(mp => Object.values(mp.permissions).some(Boolean)).length} modules accessible
                      </p>
                      <div className="perm-summary">
                        {ACTIONS.map(a => {
                          const count = role.modulePermissions.filter(mp => mp.permissions[a]).length;
                          if (!count) return null;
                          return (
                            <span key={a} className="perm-summary-chip" style={{ background: ACTION_COLORS[a].bg, color: ACTION_COLORS[a].color }}>
                              {a}: {count}
                            </span>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <p className="no-perms">No permissions assigned</p>
                  )}
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="empty-roles"><FiShield /><p>No roles found</p></div>
          )}
        </div>

        {confirm && (
          <div className="modal-overlay" onClick={() => setConfirm(null)}>
            <div className="modal" style={{ maxWidth: 380, textAlign: 'center', padding: '1.75rem' }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>🗑️</div>
              <h3 style={{ margin: '0 0 .5rem', fontSize: '1rem' }}>Delete Role</h3>
              <p style={{ color: '#64748b', fontSize: '.83rem', marginBottom: '1.25rem' }}>Delete role "{confirm.role.name}"? This cannot be undone.</p>
              <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'center' }}>
                <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setConfirm(null)}>Cancel</button>
                <button className="btn-danger" style={{ flex: 1 }} onClick={doDelete}>Delete</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editing ? `Edit Role: ${editing.name}` : 'Create New Role'}</h2>
                <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Role Name</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Manager" />
                </div>

                <div className="form-group">
                  <label>Staff Type</label>
                  <select value={form.staffType} onChange={e => setForm({ ...form, staffType: e.target.value })}>
                    <option value="office">Office Staff</option>
                    <option value="site">Site Staff</option>
                  </select>
                </div>

                <p className="perm-table-label">Module Permissions</p>
                <div className="perm-table-wrap">
                  <table className="perm-table">
                    <thead>
                      <tr>
                        <th className="perm-th-all">
                          <input
                            type="checkbox"
                            className="perm-checkbox"
                            title="Toggle all permissions"
                            checked={form.modulePermissions.every(mp => ACTIONS.every(a => mp.permissions[a]))}
                            onChange={() => {
                              const allOn = form.modulePermissions.every(mp => ACTIONS.every(a => mp.permissions[a]));
                              setForm(prev => ({
                                ...prev,
                                modulePermissions: prev.modulePermissions.map(mp => ({
                                  ...mp, permissions: ACTIONS.reduce((acc, a) => ({ ...acc, [a]: !allOn }), {}),
                                })),
                              }));
                            }}
                          />
                        </th>
                        <th>Module</th>
                        {ACTIONS.map(a => (
                          <th key={a}>
                            <div className="perm-th">
                              <span style={{ textTransform: 'capitalize' }}>{a}</span>
                              <input
                                type="checkbox"
                                className="perm-checkbox"
                                title={`Toggle all ${a}`}
                                checked={form.modulePermissions.every(mp => mp.permissions[a])}
                                onChange={() => toggleAll(a)}
                              />
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {form.modulePermissions.map(mp => (
                        <tr key={mp.module}>
                          <td className="perm-td">
                            <input
                              type="checkbox"
                              className="perm-checkbox"
                              title="Toggle all permissions for this module"
                              checked={ACTIONS.every(a => mp.permissions[a])}
                              onChange={() => toggleRow(mp.module)}
                            />
                          </td>
                          <td className="module-td">{mp.moduleName}</td>
                          {ACTIONS.map(a => (
                            <td key={a} className="perm-td">
                              <input
                                type="checkbox"
                                className="perm-checkbox"
                                checked={mp.permissions[a]}
                                onChange={() => togglePerm(mp.module, a)}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <label className="clockin-photo-toggle">
                  <input
                    type="checkbox"
                    checked={form.requireClockInPhoto}
                    onChange={e => setForm({ ...form, requireClockInPhoto: e.target.checked })}
                  />
                  <span>Require site photo upload when this role's users clock in</span>
                </label>

                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary">{editing ? 'Update Role' : 'Create Role'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
